import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SignalWifiConnectedNoInternet4Icon from "@mui/icons-material/SignalWifiConnectedNoInternet4";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import TestGatewayPaymentApi from "../../../../infrastructure/api/testGatewayPayment";
import { toJalaliFullDate } from "../../../middleware/middleware";
import { formatRial, pick } from "../../../components/payment-gateway/paymentGatewayUtils";

const TestGatewayPaymentPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("sessionId");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [acting, setActing] = useState(false);

  const loadSession = useCallback(async () => {
    if (!sessionId) {
      setError("شناسه جلسه پرداخت مشخص نیست.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await TestGatewayPaymentApi.getSession(sessionId);
      setSession(data);
      if (!pick(data, "canComplete", "CanComplete")) {
        setError(
          pick(data, "statusMessage", "StatusMessage") ||
            "این جلسه در وضعیت فعلی قابل پردازش نیست."
        );
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "بارگذاری اطلاعات پرداخت ناموفق بود. محیط تست ممکن است غیرفعال باشد.";
      setError(msg);
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const amount = useMemo(
    () => pick(session, "amount", "Amount"),
    [session]
  );
  const orderNumber = useMemo(
    () => pick(session, "orderNumber", "OrderNumber"),
    [session]
  );
  const requestedAt = useMemo(
    () => pick(session, "requestedAt", "RequestedAt"),
    [session]
  );

  const runAction = (action) => {
    if (!sessionId || acting) return;
    setActing(true);
    action(sessionId);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "grey.100",
        py: { xs: 2, sm: 4 },
        px: { xs: 1.5, sm: 2 },
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box
            sx={{
              bgcolor: "primary.main",
              color: "primary.contrastText",
              px: 2,
              py: 2.5,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              محیط تست پرداخت سولارنت
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.92 }}>
              این محیط برای بررسی فرایند پرداخت و اتصال درگاه فعال است.
            </Typography>
          </Box>

          <Stack spacing={2} sx={{ p: { xs: 2, sm: 3 } }}>
            {error && (
              <Alert severity="warning" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {session && (
              <>
                <Stack spacing={1}>
                  <Row label="پذیرنده" value="Solarent" />
                  <Row label="مبلغ" value={formatRial(amount)} />
                  <Row label="شماره سفارش" value={orderNumber || "—"} />
                  <Row
                    label="زمان"
                    value={
                      requestedAt ? toJalaliFullDate(requestedAt) : "—"
                    }
                  />
                </Stack>

                <Divider />

                <Stack spacing={1.5}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    size="large"
                    disabled={acting || !!error}
                    startIcon={<CheckCircleOutlineIcon />}
                    onClick={() => runAction(TestGatewayPaymentApi.submitSuccess)}
                  >
                    تایید پرداخت
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    size="large"
                    disabled={acting || !!error}
                    startIcon={<ErrorOutlineIcon />}
                    onClick={() => runAction(TestGatewayPaymentApi.submitFail)}
                  >
                    عدم تایید پرداخت
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="warning"
                    size="large"
                    disabled={acting || !!error}
                    startIcon={<SignalWifiConnectedNoInternet4Icon />}
                    onClick={() => runAction(TestGatewayPaymentApi.submitUnknown)}
                  >
                    اختلال ارتباط
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

const Row = ({ label, value }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="flex-start"
    gap={2}
    flexWrap="wrap"
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={600} textAlign="left">
      {value}
    </Typography>
  </Box>
);

export default TestGatewayPaymentPage;
